import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRightIcon,
    CheckIcon,
    ChevronRightIcon,
    MinusIcon,
    XMarkIcon,
    SparklesIcon,
    ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { comMarketing } from "../data/seo/kw3OnPage";
import {
    COMPARISONS_BY_SLUG,
    COMPARISON_DISCLAIMER,
    getRelatedComparisons,
    type CellTone,
    type ComparisonCell,
} from "../data/comparisons";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import {
    buildComparisonWebPageSchema,
    buildServiceSchema,
} from "../data/seo/structuredData";

/**
 * "FloGPT vs <competitor>" comparison page (e.g. /compare/flogpt-vs-vapi).
 * Data-driven from `COMPARISONS_BY_SLUG`. Renders an answer-first summary,
 * a capability matrix, an honest "when the competitor fits" block, related
 * comparisons, and a FAQ — backed by BreadcrumbList + WebPage + Service +
 * FAQPage JSON-LD for SERP rich results and AI-engine extraction.
 */

const TONE_STYLES: Record<CellTone, { Icon: typeof CheckIcon; className: string }> = {
    yes: { Icon: CheckIcon, className: "text-emerald-400" },
    partial: { Icon: MinusIcon, className: "text-amber-400" },
    no: { Icon: XMarkIcon, className: "text-slate-500" },
};

const Cell = ({ cell }: { cell: ComparisonCell }) => {
    const { Icon, className } = TONE_STYLES[cell.tone];
    return (
        <div className="flex items-start gap-2">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${className}`} aria-hidden="true" />
            <span className="text-sm leading-snug text-slate-300">{cell.text}</span>
        </div>
    );
};

const Eyebrow = ({ accent, children }: { accent: string; children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
        <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${accent}`} />
        {children}
    </div>
);

export default function ComparisonPage() {
    const { comparisonSlug } = useParams<{ comparisonSlug: string }>();
    const item = comparisonSlug ? COMPARISONS_BY_SLUG[comparisonSlug] : undefined;

    const related = useMemo(() => (item ? getRelatedComparisons(item, 3) : []), [item]);

    usePageSeo({
        title: item?.seo.title,
        description: item?.seo.description,
        keywords: item?.seo.keywords.join(", "),
        canonical: item ? `/compare/${item.slug}` : undefined,
    });

    const breadcrumbSchema = useMemo(() => {
        if (!item) return null;
        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Compare", item: `${marketingOrigin()}/compare` },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: `FloGPT vs ${item.competitor}`,
                    item: `${marketingOrigin()}/compare/${item.slug}`,
                },
            ],
        };
    }, [item]);

    const webPageSchema = useMemo(() => {
        if (!item) return null;
        return buildComparisonWebPageSchema({
            competitor: item.competitor,
            competitorDomain: item.competitorDomain,
            path: `/compare/${item.slug}`,
            name: `FloGPT vs ${item.competitor}`,
            description: item.seo.description,
            capabilities: item.rows.map((r) => r.capability),
        });
    }, [item]);

    const serviceSchema = useMemo(() => {
        if (!item) return null;
        return buildServiceSchema({
            name: `FloGPT — ${item.competitor} alternative`,
            description: item.heroSummary,
            path: `/compare/${item.slug}`,
            serviceType: "AI Chat & Voice Agents platform with built-in CRM",
        });
    }, [item]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [comparisonSlug]);

    if (!item) {
        return <Navigate to="/compare" replace />;
    }

    const Icon = item.Icon;

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                {/* Hero */}
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className={`absolute -left-32 top-10 h-72 w-72 rounded-full blur-3xl ${item.softAccent}`} />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12 lg:py-16">
                        <nav aria-label="Breadcrumb" className="text-xs text-slate-400 flex items-center gap-1.5 mb-6">
                            <Link to="/" className="hover:text-white transition-colors">Home</Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <Link to="/compare" className="hover:text-white transition-colors">Compare</Link>
                            <ChevronRightIcon className="h-3 w-3" />
                            <span className="text-slate-200">FloGPT vs {item.competitor}</span>
                        </nav>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Eyebrow accent={item.accent}>Comparison · FloGPT vs {item.competitor}</Eyebrow>

                            <div className="mt-5 flex items-center gap-4">
                                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg">
                                    <SparklesIcon className="h-6 w-6 text-white" />
                                </div>
                                <ArrowsRightLeftIcon className="h-5 w-5 text-slate-500" />
                                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${item.accent} flex items-center justify-center shadow-lg`}>
                                    <Icon className="h-6 w-6 text-white" />
                                </div>
                            </div>

                            <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.1]">
                                FloGPT vs{" "}
                                <span className={`bg-gradient-to-r ${item.accent} bg-clip-text text-transparent`}>
                                    {item.competitor}
                                </span>
                            </h1>
                            <p id="comparison-summary" className="mt-6 text-lg leading-relaxed text-slate-300 max-w-3xl">
                                {item.heroSummary}
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                                >
                                    Start free
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                    to="/contact"
                                    className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                >
                                    Talk to sales
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* What is the competitor */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 pb-8">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
                            <h2 className="text-lg font-semibold text-white">
                                What is {item.competitor}?
                            </h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                {item.competitorSummary}{" "}
                                <span className="text-slate-500">({item.competitorDomain})</span>
                            </p>
                        </div>
                    </div>
                </section>

                {/* Comparison table */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                        <div className="max-w-3xl mb-8">
                            <Eyebrow accent={item.accent}>Side by side</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                FloGPT vs {item.competitor}, capability by capability
                            </h2>
                        </div>

                        <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60">
                            <div className="grid grid-cols-3 border-b border-white/10 bg-white/5">
                                <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                                    Capability
                                </div>
                                <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-orange-400">
                                    FloGPT
                                </div>
                                <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-300">
                                    {item.competitor}
                                </div>
                            </div>
                            {item.rows.map((row, idx) => (
                                <div
                                    key={row.capability}
                                    className={`grid grid-cols-3 gap-2 px-4 py-4 ${
                                        idx % 2 === 0 ? "bg-transparent" : "bg-white/[0.02]"
                                    }`}
                                >
                                    <div className="pr-2 text-sm font-medium text-slate-200">{row.capability}</div>
                                    <Cell cell={row.flogpt} />
                                    <Cell cell={row.competitor} />
                                </div>
                            ))}
                        </div>

                        <p className="mt-4 text-xs leading-relaxed text-slate-500">{COMPARISON_DISCLAIMER}</p>
                    </div>
                </section>

                {/* Why FloGPT */}
                <section className="relative overflow-hidden">
                    <div className={`absolute inset-0 -z-10 ${item.softAccent} opacity-30`} />
                    <div className="absolute inset-0 -z-10 bg-slate-900/80" />
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16">
                        <div className="max-w-3xl mb-10">
                            <Eyebrow accent={item.accent}>Why teams choose FloGPT</Eyebrow>
                            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                One platform instead of a stack
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {item.whyFloGPT.map((reason, idx) => (
                                <motion.div
                                    key={reason.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-sm p-6"
                                >
                                    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${item.accent} flex items-center justify-center shadow-lg mb-4`}>
                                        <CheckIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{reason.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{reason.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* When the competitor is the better fit + migration */}
                <section className="relative overflow-hidden">
                    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
                                <h2 className="text-lg font-semibold text-white">
                                    When {item.competitor} is the better fit
                                </h2>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.competitorBestFor}</p>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
                                <h2 className="text-lg font-semibold text-white">Switching to FloGPT</h2>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.migrationNote}</p>
                            </div>
                        </div>

                        {comMarketing() && item.slug === "flogpt-vs-synthflow" ? (
                            <p className="mt-8 text-sm text-slate-400">
                                Side by side on the hub:{" "}
                                <Link to="/compare" className="font-medium text-orange-400 hover:text-orange-300">
                                    Synthflow vs Vapi
                                </Link>
                                .
                            </p>
                        ) : null}
                        {comMarketing() && item.slug === "flogpt-vs-vapi" ? (
                            <p className="mt-8 text-sm text-slate-400">
                                Side by side on the hub:{" "}
                                <Link to="/compare" className="font-medium text-orange-400 hover:text-orange-300">
                                    Vapi vs Synthflow
                                </Link>
                                .
                            </p>
                        ) : null}
                        <p className="mt-8 text-sm text-slate-400">
                            Explore FloGPT's{" "}
                            <Link to="/ai-voice-agents" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                AI voice agents
                            </Link>
                            ,{" "}
                            <Link to="/ai-chat-widget" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                AI chat widget
                            </Link>
                            ,{" "}
                            <Link to="/ai-knowledge-base" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                knowledge base
                            </Link>{" "}
                            and{" "}
                            <Link to="/ai-crm" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                                unified AI CRM
                            </Link>
                            .
                        </p>
                    </div>
                </section>

                {/* Related comparisons */}
                {related.length > 0 && (
                    <section className="relative overflow-hidden">
                        <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                    More comparisons
                                </h2>
                                <Link
                                    to="/compare"
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors"
                                >
                                    See all comparisons
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                {related.map((rel) => {
                                    const RelIcon = rel.Icon;
                                    return (
                                        <Link
                                            key={rel.slug}
                                            to={`/compare/${rel.slug}`}
                                            className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 hover:border-white/20 hover:bg-white/10 transition-all"
                                        >
                                            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${rel.accent} flex items-center justify-center shadow-lg mb-3`}>
                                                <RelIcon className="h-5 w-5 text-white" />
                                            </div>
                                            <h3 className="text-base font-semibold text-white group-hover:text-orange-400 transition-colors">
                                                FloGPT vs {rel.competitor}
                                            </h3>
                                            <p className="mt-1 text-sm text-slate-400 leading-relaxed line-clamp-2">{rel.tagline}</p>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                <FAQSection
                    id={`compare-${item.slug}`}
                    eyebrow={`FloGPT vs ${item.competitor} · FAQ`}
                    heading={`FloGPT vs ${item.competitor} FAQ`}
                    intro={`Common questions about choosing FloGPT as a ${item.competitor} alternative.`}
                    items={item.faqs}
                />
            </main>

            <Footer />

            {breadcrumbSchema && <JsonLd id={`breadcrumb-compare-${item.slug}`} data={breadcrumbSchema} />}
            {webPageSchema && <JsonLd id={`webpage-compare-${item.slug}`} data={webPageSchema} />}
            {serviceSchema && <JsonLd id={`service-compare-${item.slug}`} data={serviceSchema} />}
        </div>
    );
}
